import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';
import { HiCheckCircle, HiOutlineReceiptTax, HiOutlineCalendar, HiOutlineLocationMarker, 
  HiOutlineTruck, HiArrowRight, HiOutlineMail, HiExclamation, HiOutlineSparkles } from 'react-icons/hi';
import { fetchOrderById } from '../../../pages/admin/orders-controller/order-controller';
import { fetchProductById } from '../product/controllers/product-controller';
import { fetchProductsUsingFilter } from '../../admin/controllers/products-controller';

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFoundError, setNotFoundError] = useState(false);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  
  // Extract order details from location state if available
  useEffect(() => {
    if (location.state?.orderDetails) {
      setOrder(location.state.orderDetails);
      setLoading(false);
    } else if (orderId) {
      // Fetch order details if not provided in location state
      const fetchOrderDetails = async () => {
        try {
          setLoading(true);
          // Call the actual API
          const response = await fetchOrderById(orderId);
          
          if (!response || response.error) {
            throw new Error(response?.error || 'Failed to load order details');
          }
          
          setOrder(response);
          setLoading(false);
        } catch (err) {
          console.error('Error fetching order:', err);
          if (err.message.includes('404') || err.message.includes('not found')) {
            setNotFoundError(true);
          }
          setError('Unable to load your order details. Please check your email for confirmation.');
          setLoading(false);
        }
      };
      
      fetchOrderDetails();
    } else {
      setError('No order information provided');
      setLoading(false);
    }
  }, [orderId, location.state]);

  // Load products of the same category as the ordered product
  useEffect(() => {
    if (!order) return;

    const entet = order.entet || order;
    const lines = order.line || order.lines || [];
    const stateProduct = location.state?.orderedProduct;
    const orderedIds = lines.map(l => l.productId).filter(Boolean);
    if (entet.productId && !orderedIds.includes(entet.productId)) {
      orderedIds.push(entet.productId);
    }
    if (stateProduct?.id && !orderedIds.includes(stateProduct.id)) {
      orderedIds.push(stateProduct.id);
    }
    const firstProductId = orderedIds[0];

    let cancelled = false;
    const loadSuggestions = async () => {
      try {
        setSuggestionsLoading(true);

        // Find the category of the ordered product:
        // 1. from navigation state, 2. from order lines, 3. by fetching the product
        let categoryId = stateProduct?.categorieId || lines[0]?.product?.categorieId;
        if (!categoryId && firstProductId) {
          try {
            const orderedProduct = await fetchProductById(firstProductId);
            categoryId = orderedProduct?.categorieId;
          } catch (e) {
            // Could not resolve the product's category
          }
        }
        if (cancelled) return;

        // Strictly same-category suggestions — no category, no suggestions
        if (!categoryId) {
          setSuggestedProducts([]);
          return;
        }

        const mapProducts = (list) => list
          .filter(p => !orderedIds.includes(p.id))
          .map(p => ({
            id: p.id,
            name: p.name || 'Produit',
            prixVenteTTC: p.prixVenteTTC || 0,
            prixVentePromo: p.prixVentePromo || 0,
            enPromo: p.enPromo || false,
            imageUrl: p.imageUrl || [],
            stock: p.stock || 0
          }))
          // Promo products first — they convert best
          .sort((a, b) => {
            const aPromo = a.enPromo && a.prixVentePromo > 0 ? 1 : 0;
            const bPromo = b.enPromo && b.prixVentePromo > 0 ? 1 : 0;
            return bPromo - aPromo;
          });

        // Only products from the same category as the ordered product
        const response = await fetchProductsUsingFilter({
          category: categoryId,
          page: 1,
          pageSize: 12
        });
        if (cancelled) return;
        const products = mapProducts(response?.data?.list || []);

        setSuggestedProducts(products.slice(0, 8));
      } catch (err) {
        console.error('Error loading suggested products:', err);
      } finally {
        if (!cancelled) setSuggestionsLoading(false);
      }
    };

    loadSuggestions();
    return () => { cancelled = true; };
  }, [order]);

  // Reveal suggestions with a delay: first "Commande Confirmée", then the showcase
  useEffect(() => {
    if (suggestionsLoading || suggestedProducts.length === 0) return;
    const timer = setTimeout(() => {
      setShowSuggestions(true);
      // Smoothly bring the showcase into view
      setTimeout(() => {
        suggestionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 600);
    }, 1800);
    return () => clearTimeout(timer);
  }, [suggestionsLoading, suggestedProducts]);

  // Customer info from the confirmed order, used to prefill the next order form
  const getPrefillCustomer = () => {
    const entet = order?.entet || order || {};
    return {
      firstName: entet.clientFirstName || '',
      lastName: entet.clientLastName || '',
      email: entet.clientEmail || '',
      phone: entet.clientNumber || '',
      address: entet.clientAdresse || '',
      city: entet.ville || ''
    };
  };

  const handleSuggestedProductClick = (productId) => {
    navigate(`/product/${productId}/0`, {
      state: { prefillCustomer: getPrefillCustomer() }
    });
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Get city name from code
  const getCityNameByCode = (code) => {
    const tunisianCities = {
      "TUN": "Tunis",
      "SFX": "Sfax",
      "SOS": "Sousse",
      "KRN": "Kairouan",
      "BIZ": "Bizerte",
      "GBS": "Gabes",
      "AM": "Ariana",
      "GFS": "Gafsa",
      "MNS": "Monastir",
      "BA": "Ben Arous",
      "KSR": "Kasserine",
      "MDN": "Medenine",
      "NAB": "Nabeul",
      "TAN": "Tataouine",
      "BJA": "Beja",
      "JDB": "Jendouba",
      "KEF": "Kef",
      "MHD": "Mahdia",
      "SBZ": "Sidi Bouzid",
      "TZR": "Tozeur",
      "SLI": "Siliana",
      "KBL": "Kebili",
      "ZAG": "Zaghouan",
      "MAN": "Manouba",
      "Libya": "Libya"
    };
    
    return tunisianCities[code] || code;
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.2,
        duration: 0.5
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  // Render error state
  const renderErrorState = () => (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-3xl mx-auto"
    >
      <motion.div variants={itemVariants} className="bg-red-500 px-8 py-12 text-center">
        <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm mb-6">
          <HiExclamation className="h-12 w-12 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-3">
          {notFoundError ? "Commande introuvable" : "Erreur de chargement"}
        </h1>
        <p className="text-white/90 text-lg">
          {notFoundError 
            ? "La commande que vous recherchez n'existe pas ou a peut-être été supprimée."
            : "Nous n'avons pas pu récupérer les détails de votre commande. Veuillez réessayer."}
        </p>
      </motion.div>
      
      <div className="px-8 py-10">
        <motion.div variants={itemVariants} className="text-center">
          <p className="text-gray-600 mb-8">
            {notFoundError 
              ? "Vérifiez le numéro de commande et réessayez, ou contactez notre service client pour obtenir de l'aide."
              : "Il y a eu un problème lors de la récupération des détails de votre commande. Vous pouvez réessayer ou contacter notre service client."}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link
              to="/"
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-[#4983a9] hover:bg-[#171340] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4983a9]"
            >
              Retour à l'accueil
            </Link>
            
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4983a9]"
            >
              Explorer nos produits
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
  
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      <main className="flex-grow pt-40 pb-16">
        <div className="container mx-auto px-4 max-w-5xl">
          {loading ? (
            <div className="flex justify-center items-center py-32">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#4983a9]"></div>
            </div>
          ) : error ? (
            renderErrorState()
          ) : order ? (
            <>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="bg-white rounded-2xl shadow-xl overflow-hidden"
            >
              {/* Success Header */}
              <motion.div variants={itemVariants} className="bg-gradient-to-r from-[#4983a9] to-[#297D74] px-8 py-12 text-center">
                <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm mb-6">
                  <HiCheckCircle className="h-12 w-12 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-3">Commande Confirmée!</h1>
                <p className="text-white/90 text-lg">
                  Merci pour votre commande.
                </p>
                <p className="text-white/90 text-lg mt-1" dir="rtl" lang="ar">
                  تم تأكيد طلبك، شكرا لك!
                </p>
              </motion.div>
              
              {/* Order Info */}
              
            </motion.div>

            {/* Suggested products from the same category — revealed after confirmation */}
            <div ref={suggestionsRef} style={{ scrollMarginTop: '110px' }}>
            <AnimatePresence>
            {showSuggestions && suggestedProducts.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 80, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="mt-12 relative"
              >
                {/* Glowing showcase card */}
                <div className="relative rounded-3xl p-[2px] bg-gradient-to-r from-[#4983a9] via-[#42BFDD] to-[#297D74] shadow-2xl overflow-hidden">
                  {/* Animated shimmer on the border */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                    initial={{ x: '-100%' }}
                    animate={{ x: '200%' }}
                    transition={{ repeat: Infinity, duration: 2.5, ease: 'linear', repeatDelay: 1.5 }}
                  />

                  <div className="relative rounded-3xl bg-white px-5 py-8 md:px-10 md:py-10">
                    {/* Section header */}
                    <div className="text-center mb-8">
                      <motion.div
                        initial={{ scale: 0, rotate: -10 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.3, type: 'spring', stiffness: 260, damping: 14 }}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#4983a9] to-[#297D74] text-white text-sm font-semibold mb-4 shadow-lg"
                      >
                        <motion.span
                          animate={{ rotate: [0, 20, -20, 0] }}
                          transition={{ repeat: Infinity, duration: 2, repeatDelay: 1 }}
                        >
                          <HiOutlineSparkles className="h-4 w-4" />
                        </motion.span>
                        Offre spéciale pour vous • عرض خاص لك
                      </motion.div>

                      <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.45, duration: 0.6 }}
                        className="text-2xl md:text-3xl font-bold text-gray-900"
                      >
                        Vous aimerez aussi ces produits
                      </motion.h2>

                      <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55, duration: 0.6 }}
                        className="text-xl md:text-2xl font-bold text-gray-800 mt-1"
                        dir="rtl"
                        lang="ar"
                      >
                        ستعجبك أيضا هذه المنتجات
                      </motion.h2>

                      <motion.p
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                        className="text-gray-500 mt-2 max-w-2xl mx-auto"
                      >
                        Profitez-en avant qu'il ne soit trop tard — vos informations sont déjà enregistrées, commandez en un clic !
                      </motion.p>

                      <motion.p
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7, duration: 0.6 }}
                        className="text-gray-500 mt-1 max-w-2xl mx-auto"
                        dir="rtl"
                        lang="ar"
                      >
                        اغتنم الفرصة قبل فوات الأوان — معلوماتك مسجلة بالفعل، اطلب بنقرة واحدة!
                      </motion.p>

                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: '4rem' }}
                        transition={{ delay: 0.75, duration: 0.5 }}
                        className="h-1 rounded-full mx-auto mt-4 bg-gradient-to-r from-[#4983a9] to-[#297D74]"
                      />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                      {suggestedProducts.map((product, index) => {
                        const hasImage = product.imageUrl && product.imageUrl.length > 0 && product.imageUrl[0];
                        const hasPromo = product.enPromo && product.prixVentePromo > 0;
                        const promoPercent = hasPromo && product.prixVenteTTC > 0
                          ? Math.round(((product.prixVenteTTC - product.prixVentePromo) / product.prixVenteTTC) * 100)
                          : 0;
                        const placeholderSvg = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='400'%3E%3Crect width='300' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='14'%3ENo Image%3C/text%3E%3C/svg%3E";

                        return (
                          <motion.div
                            key={product.id}
                            initial={{ opacity: 0, y: 40, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ delay: 0.8 + index * 0.15, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                            whileHover={{ y: -6 }}
                            onClick={() => handleSuggestedProductClick(product.id)}
                            className="group cursor-pointer"
                          >
                            <div className="relative overflow-hidden rounded-2xl bg-gray-50 aspect-[3/4] mb-3 shadow-sm group-hover:shadow-xl transition-shadow duration-500">
                              <img
                                src={hasImage ? product.imageUrl[0] : placeholderSvg}
                                alt={product.name}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                onError={(e) => { e.target.src = placeholderSvg; }}
                                loading="lazy"
                              />

                              {/* Hover overlay */}
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-500" />

                              {/* Promo badge — pops in with a bounce */}
                              {hasPromo && (
                                <motion.div
                                  initial={{ scale: 0, rotate: -15 }}
                                  animate={{ scale: 1, rotate: 0 }}
                                  transition={{ delay: 1.1 + index * 0.15, type: 'spring', stiffness: 300, damping: 12 }}
                                  className="absolute top-3 left-3"
                                >
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold text-white bg-red-500 shadow-lg">
                                    -{promoPercent}%
                                  </span>
                                </motion.div>
                              )}

                              {/* CTA on hover */}
                              <div className="absolute inset-x-0 bottom-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out">
                                <div className="w-full py-2 rounded-xl text-white text-sm font-semibold text-center backdrop-blur-md bg-gradient-to-r from-[#4983a9]/90 to-[#297D74]/90 flex flex-col items-center justify-center leading-tight">
                                  <span className="flex items-center gap-1.5">
                                    Commander en 1 clic
                                    <HiArrowRight className="h-4 w-4" />
                                  </span>
                                  <span dir="rtl" lang="ar" className="text-xs opacity-90">اطلب بنقرة واحدة</span>
                                </div>
                              </div>
                            </div>

                            <div className="px-1">
                              <h3 className="text-sm font-medium text-gray-900 group-hover:text-[#297D74] transition-colors duration-300 line-clamp-2 leading-snug mb-1.5">
                                {product.name}
                              </h3>
                              <div className="flex items-center gap-2 flex-wrap">
                                {hasPromo ? (
                                  <>
                                    <span className="text-base font-bold text-red-500">
                                      {product.prixVentePromo.toFixed(2)} TND
                                    </span>
                                    <span className="text-sm text-gray-400 line-through">
                                      {product.prixVenteTTC.toFixed(2)} TND
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-base font-bold text-gray-900">
                                    {product.prixVenteTTC.toFixed(2)} TND
                                  </span>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            </AnimatePresence>
            </div>
            </>
          ) : (
            renderErrorState() // Also show error state if order is null but not loading
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default OrderConfirmation;